<template>
  <div>
    <!-- Settings Information Card -->
    <v-card variant="outlined" class="pa-2">
      <v-card-title class="text-subtitle-1 mb-1 pa-2">
        <v-icon class="me-1" size="small">mdi-cog</v-icon>
        Settings Information
      </v-card-title>
      <v-form ref="settingsForm" class="pa-1">
        <v-row class="ma-0">
          <!-- Note: Personal information fields (Gender, Blood Group, Religion, Marital Status, Emergency Contact) 
               have been moved to the Personal Information tab to avoid duplication -->
        </v-row>
      </v-form>
    </v-card>

    <!-- Overtime (OT) Information Section -->
    <v-card variant="outlined" class="pa-2 mt-1">
      <v-card-title class="text-subtitle-1 mb-1 pa-2">
        <v-icon class="me-1" size="small">mdi-clock-outline</v-icon>
        Overtime (OT) Information
      </v-card-title>
      <v-form ref="otForm" class="pa-1">
        <v-row class="ma-0">
          <v-col cols="12" md="6" class="pa-1">
            <v-select
              v-model="settingsInfo.ot_applicable"
              label="OT Applicable"
              variant="outlined"
              density="compact"
              hide-details="auto"
              :items="[
                { text: 'No', value: 'no' },
                { text: 'Yes', value: 'yes' }
              ]"
              item-title="text"
              item-value="value"
              clearable
            ></v-select>
          </v-col>
        </v-row>

        <!-- OT Configuration - Show when OT is applicable -->
        <v-row v-if="showOTFields" class="ma-0">
          <!-- Daily OT Column -->
          <v-col cols="12" md="6" class="pa-1">
            <v-card variant="outlined" class="pa-3">
              <v-card-title class="text-subtitle-2 mb-2 pa-0">
                <v-icon class="me-1" size="small">mdi-calendar-today</v-icon>
                Daily OT Configuration
              </v-card-title>
              
              <v-select
                v-model="settingsInfo.daily_ot_type"
                label="Daily OT Type"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :items="[
                  { text: 'Percentage', value: 'percentage' },
                  { text: 'Fixed', value: 'fixed' },
                  { text: 'Amount', value: 'amount' }
                ]"
                item-title="text"
                item-value="value"
                clearable
                class="mb-2"
              ></v-select>

              <!-- Daily OT Percentage Fields -->
              <div v-if="showDailyOTPercentageFields">
                <v-text-field
                  v-model="settingsInfo.daily_ot_rate"
                  label="Daily OT Rate (%)"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  type="number"
                  suffix="%"
                  class="mb-2"
                  hint="Percentage of daily rate (Basic or Gross)"
                  persistent-hint
                ></v-text-field>
                <v-select
                  v-model="settingsInfo.daily_ot_pay_on"
                  label="Daily OT Pay On"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :items="[
                    { text: 'Basic', value: 'basic' },
                    { text: 'Gross', value: 'gross' }
                  ]"
                  item-title="text"
                  item-value="value"
                  clearable
                  hint="Calculate OT based on Basic or Gross salary"
                  persistent-hint
                ></v-select>
              </div>

              <!-- Daily OT Amount Field -->
              <div v-if="showDailyOTAmountField">
                <v-text-field
                  v-model="settingsInfo.daily_ot_amount"
                  label="Daily OT Amount"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  type="number"
                  prefix="৳"
                  hint="Fixed amount for daily OT"
                  persistent-hint
                ></v-text-field>
              </div>

              <!-- Daily OT Fixed Rate Field -->
              <div v-if="showDailyOTFixedFields">
                <v-text-field
                  v-model="settingsInfo.daily_ot_rate"
                  label="Daily OT Rate"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  type="number"
                  prefix="৳"
                  hint="Fixed rate per day regardless of salary"
                  persistent-hint
                ></v-text-field>
              </div>
            </v-card>
          </v-col>

          <!-- Hourly OT Column -->
          <v-col cols="12" md="6" class="pa-1">
            <v-card variant="outlined" class="pa-3">
              <v-card-title class="text-subtitle-2 mb-2 pa-0">
                <v-icon class="me-1" size="small">mdi-clock-outline</v-icon>
                Hourly OT Configuration
              </v-card-title>
              
              <v-select
                v-model="settingsInfo.hourly_ot_type"
                label="Hourly OT Type"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :items="[
                  { text: 'Percentage', value: 'percentage' },
                  { text: 'Fixed', value: 'fixed' },
                  { text: 'Amount', value: 'amount' }
                ]"
                item-title="text"
                item-value="value"
                clearable
                class="mb-2"
              ></v-select>

              <!-- Hourly OT Percentage Fields -->
              <div v-if="showHourlyOTPercentageFields">
                <v-text-field
                  v-model="settingsInfo.hourly_ot_rate_percentage"
                  label="Hourly OT Rate (%)"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  type="number"
                  suffix="%"
                  class="mb-2"
                  hint="Percentage of hourly rate (Basic or Gross)"
                  persistent-hint
                ></v-text-field>
                <v-select
                  v-model="settingsInfo.hourly_ot_pay_on"
                  label="Hourly OT Pay On"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :items="[
                    { text: 'Basic', value: 'basic' },
                    { text: 'Gross', value: 'gross' }
                  ]"
                  item-title="text"
                  item-value="value"
                  clearable
                  hint="Calculate OT based on Basic or Gross salary"
                  persistent-hint
                ></v-select>
              </div>

              <!-- Hourly OT Fixed Rate Field -->
              <div v-if="settingsInfo.hourly_ot_type === 'fixed'">
                <v-text-field
                  v-model="settingsInfo.hourly_ot_rate"
                  label="Hourly OT Rate"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  type="number"
                  prefix="৳"
                  hint="Fixed rate per hour regardless of salary"
                  persistent-hint
                ></v-text-field>
              </div>

              <!-- Hourly OT Amount Field -->
              <div v-if="showHourlyOTAmountField">
                <v-text-field
                  v-model="settingsInfo.hourly_ot_amount"
                  label="Hourly OT Amount"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  type="number"
                  prefix="৳"
                  hint="Fixed amount for hourly OT"
                  persistent-hint
                ></v-text-field>
              </div>
            </v-card>
          </v-col>
        </v-row>


      </v-form>
    </v-card>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { toast } from 'vue3-toastify'
import { hrmService } from '@/services/hrmService'

// Props
const props = defineProps({
  settingsInfo: {
    type: Object,
    required: true
  },
  employeeId: {
    type: [String, Number],
    required: true
  }
})

// Emits
const emit = defineEmits(['update:settingsInfo'])

// Store
const store = useStore()

// Local refs
const settingsForm = ref(null)
const otForm = ref(null)
const dataLoaded = ref(false)

// Computed properties for conditional rendering
const showOTFields = computed(() => {
  return props.settingsInfo.ot_applicable === 'yes'
})

const showDailyOTPercentageFields = computed(() => {
  return showOTFields.value && props.settingsInfo.daily_ot_type === 'percentage'
})

const showDailyOTAmountField = computed(() => {
  return showOTFields.value && props.settingsInfo.daily_ot_type === 'amount'
})

const showDailyOTFixedFields = computed(() => {
  return showOTFields.value && props.settingsInfo.daily_ot_type === 'fixed'
})

const showHourlyOTPercentageFields = computed(() => {
  return showOTFields.value && props.settingsInfo.hourly_ot_type === 'percentage'
})

const showHourlyOTAmountField = computed(() => {
  return showOTFields.value && props.settingsInfo.hourly_ot_type === 'amount'
})

const showHourlyOTFields = computed(() => {
  return showOTFields.value
})

// Load settings information data
const loadSettingsInfo = async () => {
  if (!props.employeeId) return
  
  try {
    const response = await hrmService.getSettingsInfoByEmployee(props.employeeId)
    
    if (response.success && response.data) {
      // Update the settingsInfo with loaded data
      const loadedData = response.data
      emit('update:settingsInfo', {
        ...props.settingsInfo,
        ...loadedData
      })
      dataLoaded.value = true
    } else {
      // Initialize with default values if no data exists
      dataLoaded.value = true
    }
  } catch (error) {
    console.error('Error loading settings info:', error)
    // Initialize with default values on error
    dataLoaded.value = true
  }
}

// Save settings information data
const saveSettingsInfo = async () => {
  if (!props.employeeId) {
    toast.error('Employee ID is required')
    return
  }

  try {
    const response = await hrmService.saveEmployeeSettingsInfo(props.employeeId, props.settingsInfo)
    
    if (response.success) {
      toast.success('Settings information saved successfully')
      // Update with the saved data
      if (response.data) {
        emit('update:settingsInfo', {
          ...props.settingsInfo,
          ...response.data
        })
      }
    } else {
      throw new Error(response.message || 'Failed to save settings information')
    }
  } catch (error) {
    console.error('Error saving settings info:', error)
    toast.error('Failed to save settings information')
  }
}

// Watch for changes and emit updates
watch(() => props.settingsInfo, (newValue) => {
  emit('update:settingsInfo', newValue)
}, { deep: true })

// Watch for employee ID changes
watch(() => props.employeeId, (newId) => {
  if (newId && !dataLoaded.value) {
    loadSettingsInfo()
  }
}, { immediate: true })

// Watch for Daily OT Type changes and clear irrelevant fields
watch(() => props.settingsInfo.daily_ot_type, (newType, oldType) => {
  if (newType !== oldType && oldType) {
    // Clear all fields first
    const updatedInfo = { ...props.settingsInfo }
    
    // Clear based on new type - only keep relevant fields
    if (newType === 'percentage') {
      // Clear fixed and amount fields
      updatedInfo.daily_ot_amount = null
    } else if (newType === 'fixed') {
      // Clear percentage-specific and amount fields
      updatedInfo.daily_ot_pay_on = null
      updatedInfo.daily_ot_amount = null
    } else if (newType === 'amount') {
      // Clear percentage and fixed fields
      updatedInfo.daily_ot_rate = null
      updatedInfo.daily_ot_pay_on = null
    } else if (!newType) {
      // If cleared completely, clear all related fields
      updatedInfo.daily_ot_rate = null
      updatedInfo.daily_ot_pay_on = null
      updatedInfo.daily_ot_amount = null
    }
    
    emit('update:settingsInfo', updatedInfo)
  }
})

// Watch for Hourly OT Type changes and clear irrelevant fields
watch(() => props.settingsInfo.hourly_ot_type, (newType, oldType) => {
  if (newType !== oldType && oldType) {
    // Clear all fields first
    const updatedInfo = { ...props.settingsInfo }
    
    // Clear based on new type - only keep relevant fields
    if (newType === 'percentage') {
      // Clear fixed and amount fields
      updatedInfo.hourly_ot_rate = null
      updatedInfo.hourly_ot_amount = null
    } else if (newType === 'fixed') {
      // Clear percentage-specific and amount fields
      updatedInfo.hourly_ot_rate_percentage = null
      updatedInfo.hourly_ot_pay_on = null
      updatedInfo.hourly_ot_amount = null
    } else if (newType === 'amount') {
      // Clear percentage and fixed fields
      updatedInfo.hourly_ot_rate = null
      updatedInfo.hourly_ot_rate_percentage = null
      updatedInfo.hourly_ot_pay_on = null
    } else if (!newType) {
      // If cleared completely, clear all related fields
      updatedInfo.hourly_ot_rate = null
      updatedInfo.hourly_ot_rate_percentage = null
      updatedInfo.hourly_ot_pay_on = null
      updatedInfo.hourly_ot_amount = null
    }
    
    emit('update:settingsInfo', updatedInfo)
  }
})

// Load data on mount
onMounted(() => {
  if (props.employeeId && !dataLoaded.value) {
    loadSettingsInfo()
  }
})

// Expose form validation methods and save function
defineExpose({
  validateSettings: () => settingsForm.value?.validate(),
  validateOT: () => otForm.value?.validate(),
  validateAll: async () => {
    const results = await Promise.all([
      settingsForm.value?.validate(),
      otForm.value?.validate()
    ])
    return results.every(result => result?.valid !== false)
  },
  saveSettingsInfo,
  loadSettingsInfo,
})
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
